/**
 * InformeMesDoc — el cuadro del mes para UNA compañía, una fila por día.
 *
 * TODOS LOS DÍAS SON FILA, con o sin movimiento. Un cuadro que sólo lista los días con
 * plata obliga a la aseguradora a ir contando cuáles faltan, y un día ausente se lee igual
 * que un día olvidado.
 *
 * UN DÍA SIN MOVIMIENTO DICE POR QUÉ, en la columna del total y en cursiva. El diseño
 * original lo explicaba por fin de semana o feriado; eso es falso — se cobra los siete días
 * del calendario y el PSP procesa sábados. Los días hábiles sólo mandan en la fecha del
 * giro.
 *
 * @see src/components/payments/yoolpay/informe/documento.js
 * @see docs/superpowers/specs/2026-08-14-informes-liquidacion-design.md §3.1
 */
import React from 'react';

import { formatearRut } from '../../../../lib/rut';
import HojaA4, { Caja, Dato, Nota, RejillaDatos } from './HojaA4';
import {
  TINTA,
  fmtClpInforme,
  fmtFechaDoc,
  fmtPeriodoDoc,
  fmtUf,
  hoyDoc,
  textoMotivo,
} from './documento';

const NUMERO = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const th = (extra = {}) => ({
  textAlign: 'left',
  fontSize: 8,
  fontWeight: 700,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: TINTA.rotulo,
  borderBottom: `1.5px solid ${TINTA.texto}`,
  padding: '0 8px 5px 0',
  ...extra,
});

const td = (extra = {}) => ({
  fontSize: 9.5,
  padding: '5px 8px 5px 0',
  borderBottom: '1px solid #F0F0EE',
  ...extra,
});

const total = (extra = {}) => ({
  fontSize: 10.5,
  fontWeight: 700,
  padding: '8px 8px 0 0',
  borderTop: `2px solid ${TINTA.texto}`,
  ...extra,
});

/** El pie institucional, con lo que el emisor tenga configurado. */
function pieEmisor(emisor = {}) {
  return [
    emisor.razon_social,
    emisor.rut ? `RUT ${formatearRut(emisor.rut)}` : null,
    'Recaudación por cuenta de terceros',
    'YoolPay',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Una línea de firma. */
function Firma({ children }) {
  return (
    <div style={{ borderTop: `1px solid ${TINTA.texto}`, paddingTop: 6 }}>
      <p style={{ margin: 0, fontSize: 9, color: TINTA.tenue }}>{children}</p>
    </div>
  );
}

/**
 * La celda de plata de un día.
 *
 * `null` queda vacía y cero se dice con palabras («sin venta nueva»). Son cosas distintas:
 * cero significa que ese día hubo cobranza pero no venta nueva; vacío, que ese día no hubo
 * nada que informar.
 */
function CeldaMonto({ valor, vacio }) {
  if (valor == null) return <td style={td(NUMERO)}>{''}</td>;
  if (valor === 0) return <td style={td({ ...NUMERO, color: TINTA.tenue })}>{vacio}</td>;
  return <td style={td(NUMERO)}>{fmtClpInforme(valor)}</td>;
}

export default function InformeMesDoc({ informe }) {
  if (!informe) return null;

  const dias = informe.dias ?? [];
  const primero = dias[0]?.fecha ?? null;
  const ultimo = dias[dias.length - 1]?.fecha ?? null;
  const sinMovimiento = dias.length - (informe.dias_con_movimiento ?? 0);

  return (
    <HojaA4
      rotulo={`Informe de liquidación · ${informe.compania_nombre ?? 'sin compañía'}`}
      eyebrow="Informe 2 — Liquidación del mes"
      titulo={fmtPeriodoDoc(informe.period)}
      pie={pieEmisor(informe.emisor)}
    >
      <RejillaDatos>
        <Dato
          rotulo="Compañía"
          valor={informe.compania_nombre ?? 'Sin clasificar'}
          bajada={informe.compania_rut ? `RUT ${formatearRut(informe.compania_rut)}` : null}
        />
        <Dato
          rotulo="Período"
          valor={`${fmtFechaDoc(primero)} a ${fmtFechaDoc(ultimo)}`}
          bajada={`${dias.length} días`}
        />
        <Dato
          rotulo="Días con movimiento"
          valor={`${informe.dias_con_movimiento} de ${dias.length}`}
          bajada={`${sinMovimiento} sin movimiento`}
        />
        <Dato
          rotulo="Informe"
          valor={informe.numero}
          bajada={`emitido ${fmtFechaDoc(hoyDoc())}`}
        />
      </RejillaDatos>

      <table style={{ width: '100%', borderCollapse: 'collapse', margin: '14px 0 0' }}>
        <thead>
          <tr>
            <th style={th({ width: '11%' })}>Día</th>
            <th style={th(NUMERO)}>Venta nueva CLP</th>
            <th style={th(NUMERO)}>Cobranza CLP</th>
            <th style={th({ ...NUMERO, width: '11%' })}>Total UF</th>
            <th style={th(NUMERO)}>Total cobrado CLP</th>
            <th style={th(NUMERO)}>Girado CLP</th>
            <th style={th({ ...NUMERO, width: '13%', padding: '0 0 5px 0' })}>Fecha del giro</th>
          </tr>
        </thead>
        <tbody>
          {dias.map((f) => {
            const hubo = f.total_clp != null;
            return (
              <tr key={f.fecha}>
                <td
                  style={td({
                    color: TINTA.suave,
                    fontVariantNumeric: 'tabular-nums',
                    paddingLeft: 0,
                  })}
                >
                  {f.dia_semana} {f.fecha.slice(8)}
                </td>
                <CeldaMonto valor={f.venta_nueva_clp} vacio="sin venta nueva" />
                <CeldaMonto valor={f.cobranza_clp} vacio="sin cobranza" />
                <td style={td(NUMERO)}>{hubo ? fmtUf(f.uf) : ''}</td>
                {/* LA COLUMNA DEL TOTAL LLEVA EL MOTIVO cuando no hubo plata: es la
                    explicación, y va donde iría la cifra que falta. */}
                <td
                  style={td(
                    hubo
                      ? { ...NUMERO, fontWeight: 600 }
                      : { ...NUMERO, color: TINTA.tenue, fontStyle: 'italic' },
                  )}
                >
                  {hubo ? fmtClpInforme(f.total_clp) : textoMotivo(f.motivo)}
                </td>
                <td
                  style={td(
                    f.girado_clp == null && hubo ? { ...NUMERO, color: TINTA.tenue } : NUMERO,
                  )}
                >
                  {f.girado_clp != null
                    ? fmtClpInforme(f.girado_clp)
                    : (hubo ? 'pendiente' : '')}
                </td>
                <td style={td({ ...NUMERO, color: TINTA.suave, padding: '5px 0' })}>
                  {f.fecha_giro ? fmtFechaDoc(f.fecha_giro) : ''}
                </td>
              </tr>
            );
          })}
          <tr>
            <td
              style={total({
                fontSize: 9.5,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                paddingLeft: 0,
              })}
            >
              Total
            </td>
            <td style={total(NUMERO)}>{fmtClpInforme(informe.total.venta_nueva_clp)}</td>
            <td style={total(NUMERO)}>{fmtClpInforme(informe.total.cobranza_clp)}</td>
            <td style={total(NUMERO)}>{fmtUf(informe.total.uf)}</td>
            <td style={total(NUMERO)}>{fmtClpInforme(informe.total.cobrado_clp)}</td>
            <td style={total(NUMERO)}>{fmtClpInforme(informe.total.girado_clp)}</td>
            <td style={total({ padding: '8px 0 0' })} />
          </tr>
        </tbody>
      </table>

      <Caja rotulo="Lectura del cuadro" style={{ margin: '18px 0 0' }}>
        <Nota>
          Un día sin movimiento no es un error: se informa con el motivo que la operación
          registró —sin vencimientos, cuotas fallidas, o cobranza de la propia compañía— y
          no con un cero, porque no hay monto que conciliar.
        </Nota>
        <Nota>
          <strong>
            Se cobra cualquier día del calendario, incluidos sábados y domingos.
          </strong>{' '}
          Los días hábiles sólo determinan la fecha del giro: el depósito neto del
          procesador se acredita en T+3 hábiles y la transferencia sale al día hábil
          siguiente.
        </Nota>
        <Nota style={{ margin: 0 }}>
          Cada día se gira completo y por separado: el total cobrado de la jornada es el
          monto girado de esa jornada.
        </Nota>
      </Caja>

      <div
        style={{
          breakInside: 'avoid',
          margin: '12px 0 0',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        <Caja rotulo="Cobrado del mes" acento style={{ margin: 0 }}>
          <Nota style={{ margin: 0, fontSize: 10 }}>
            {fmtUf(informe.total.uf)} UF · {fmtClpInforme(informe.total.cobrado_clp)} CLP en{' '}
            {informe.dias_con_movimiento}{' '}
            {informe.dias_con_movimiento === 1 ? 'jornada' : 'jornadas'}.
          </Nota>
        </Caja>
        <Caja rotulo="Girado del mes" style={{ margin: 0 }}>
          <Nota style={{ margin: 0, fontSize: 10 }}>
            {fmtClpInforme(informe.total.girado_clp)} CLP en {informe.giros_emitidos}{' '}
            {informe.giros_emitidos === 1 ? 'transferencia' : 'transferencias'}.
          </Nota>
        </Caja>
      </div>

      <div
        style={{
          breakInside: 'avoid',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 40,
          margin: '34px 0 0',
        }}
      >
        <Firma>Preparado por · {informe.emisor?.razon_social ?? 'Yool Digital SpA'}</Firma>
        <Firma>Conforme · {informe.compania_nombre ?? 'la compañía'}</Firma>
      </div>

      {informe.truncado ? (
        <Nota style={{ margin: '12px 0 0', color: TINTA.suave }}>
          Este informe alcanzó el tope de filas por consulta y puede estar incompleto.
        </Nota>
      ) : null}
    </HojaA4>
  );
}
